import { decodeString } from "@findemdev/findem-helpers";
import { ALIGN_OPTION_VALUES, GroupedAssignments, UniquelyIdentifiedAssigmentsTableData } from "../types/common";
import { TEMPLATE_META_DATA } from "../../../../types/credential";

export const LAYOUT_ASSIGNMENTS_PER_ROW_TEMPLATE_STANDARD: Record<string, string | number>[] = [
  {
    value: 5,
    label: '5'
  },
  {
    value: 4,
    label: '4'
  },
  {
    value: 3,
    label: '3'
  },
  {
    value: 2,
    label: '2'
  }
];

export const DEFAULT_ASSIGNMENTS_COL_TEMPLATE_STANDARD = 5;
export const DEFAULT_ASSIGNMENTS_ROW_TEMPLATE_STANDARD = 4;
export const MAX_COLS_TEMAPLTE_STANDARD = 5;
export const MAX_APPLIED_FIELDS = 3;
export const GRID_AREA_PREFIX = 'grid_area_';
export const GROUPED_ASSIGNMENTS_REFS_DELIMITER = '-';
export const MANUAL_CREATED_ASSIGNMENT_SUFFIX = 'manual'; // all manually created assignments should have this in their ids/refs

export const generateGridTemplateColumnsTemplateStandard = ({
  colsPerRow,
  maxColsPerRow = MAX_COLS_TEMAPLTE_STANDARD,
  tilesAlignment,
}: {
  colsPerRow: number;
  maxColsPerRow: number;
  tilesAlignment: TEMPLATE_META_DATA['tiles_alignment'];
}): string => {
  if (colsPerRow === maxColsPerRow || tilesAlignment === ALIGN_OPTION_VALUES.LEFT) {
    return `repeat(${maxColsPerRow}, 1fr)`; // All columns are equal
  } else if (colsPerRow < maxColsPerRow) {
    const offset = (maxColsPerRow - colsPerRow) * 0.5; // Calculate fractional offset
    return `${offset}fr repeat(${colsPerRow}, 1fr) ${offset}fr`;
  }
  return decodeString(`repeat(${maxColsPerRow}, 1fr)`);
};
export const createGridTemplateAreasTemplateStandard = ({
  rows,
  columns,
  prefix,
  value,
  maxColumns = MAX_COLS_TEMAPLTE_STANDARD,
  tilesAlignment,
}: {
  rows: number,
  columns: number,
  prefix: string,
  value: string[],
  maxColumns: number; // max columns in the layout
  tilesAlignment: TEMPLATE_META_DATA['tiles_alignment'];
}): string => {
  const areas = [];
  let count = 0;

  for (let r = 0; r < rows; r++) {
    const rowAreas = [];

    // Add an empty column at the start if necessary
    if (maxColumns > columns && tilesAlignment === ALIGN_OPTION_VALUES.CENTER) {
      rowAreas.push('.');
    }

    // Fill actual grid areas
    for (let c = 0; c < columns; c++) {
      const gridAreaName = value[count] ? `${prefix}${value[count]}` : '.';

      rowAreas.push(gridAreaName);
      count++;
    }

    // Add an empty column at the end if necessary
    if (maxColumns > columns && tilesAlignment === ALIGN_OPTION_VALUES.CENTER) {
      rowAreas.push('.');
    }

    areas.push(`'${rowAreas.join(' ')}'`);
  }

  return decodeString(areas.join(' '));
};

// extract assignment ids from css layout
export const extractUngroupedAssignmentsIdsFromStandardGridTemplateAreas = ({
  gridTemplateAreas,
  prefix,
}: {
  gridTemplateAreas: string;
  prefix: string;
}) => {
  const regex = new RegExp(`${prefix}(\\d+)`, 'g');
  const matches = gridTemplateAreas.matchAll(regex);

  const values = Array.from(matches, match => match[1]);

  return values;
}

// Orders assignment based on the current css layout e.g. in the Presentation Preview/PPT download...etc
export const getUngroupedOrderedAssignmentsFromLayout = ({
  gridTemplateAreas,
  assignments,
  prefix,
}: {
  gridTemplateAreas: string,
  assignments: UniquelyIdentifiedAssigmentsTableData[],
  prefix: string,
}): {
  assignmentIds: string[],
  assignmentByIds: Record<string, UniquelyIdentifiedAssigmentsTableData>,
} => {
  const assignmentIds = extractUngroupedAssignmentsIdsFromStandardGridTemplateAreas({ gridTemplateAreas, prefix });
  const assignmentByIds = assignments?.reduce((acc, value) => {
    acc[value.ref] = value;
    return acc;
  }, {} as Record<string, UniquelyIdentifiedAssigmentsTableData>) ?? {};

  return {
    assignmentIds,
    assignmentByIds
  };
};

// extract assignment ids from css layout
/**
 * @example ['1234-5678-1234', ['1234'], ['1234-4567']]
 */
export const extractGroupedAssignmentsIdsFromStandardGridTemplateAreas = ({
  gridTemplateAreas,
  groupedAssignmentsRefsDelimiter: _groupedAssignmentsRefsDelimiter,
  prefix,
}: {
  gridTemplateAreas: string;
  groupedAssignmentsRefsDelimiter: string;
  prefix: string;
}): string[] => {
  const matches = decodeString(gridTemplateAreas)
    .replaceAll("'", "") // %27 -> ' char when decoded, these should be removed since they are not part of assignment refs
    .matchAll(new RegExp(`${prefix}([^\\s]+)`, 'g')); // gets all the characters after the prefix breaking on whitespace
  const values = Array
    .from(matches, match => match[1]);
  // .map((value) => value.split(groupedAssignmentsRefsDelimiter))
  // .flat();

  return values;
}

// Orders assignment based on the current css layout e.g. in the Presentation Preview/PPT download...etc
export const getGroupedOrderedAssignmentsFromLayout = ({
  gridTemplateAreas,
  groupedAssignments,
  groupedAssignmentsRefsDelimiter,
  prefix,
}: {
  gridTemplateAreas: string;
  groupedAssignments: GroupedAssignments[];
  groupedAssignmentsRefsDelimiter: string;
  prefix: string;
}): {
  groupedAssignmentIds: string[];
  groupedAssignmentsById: Record<string, GroupedAssignments>;
} => {
  const groupedAssignmentIds = extractGroupedAssignmentsIdsFromStandardGridTemplateAreas({ gridTemplateAreas, groupedAssignmentsRefsDelimiter, prefix });

  const groupedAssignmentsById: Record<string, GroupedAssignments> = {};
  for (const groupedAssignmentId of groupedAssignmentIds) {
    const assigmentIds = groupedAssignmentId.split(groupedAssignmentsRefsDelimiter);
    const assignmentId = assigmentIds[0]; // pick the 1st assignment ref and check if it exists in grouped assignments

    // this is guaranteed to find a match assuming the layout was generated correctly
    const groupedAssignment = groupedAssignments.find((datum) => assignmentId in datum);
    if (groupedAssignment) {
      groupedAssignmentsById[groupedAssignmentId] = groupedAssignment;
      continue;
    }
  }

  return {
    groupedAssignmentIds,
    groupedAssignmentsById,
  };
};